import { useMediaQuery, useTheme } from '@mui/material';
import type {
  GridColumnVisibilityModel,
  GridPaginationModel,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SeparatedDataGrid } from '~/components/data-grid';
import { DataGridPagination } from '~/components/data-grid/pagination';
import { EmptyState } from '~/components/empty-state';
import { usePaginationParams } from '~/hooks/pagination';
import ActivateDeviceIllustration from '~/images/illustrations/activate-device.svg';
import type { DeviceCommands__DeviceCommand as DeviceCommand } from '../../../../queries/commands.generated';
import { DetailsDialog } from '../components';
import { useColumns } from '../lib/commandColumns';

const NoRowsOverlay = () => {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration={ActivateDeviceIllustration}
      description={t('pages.devices.show.command_history.empty_state')}
    />
  );
};
export interface TableProps {
  deviceCommands: readonly DeviceCommand[];
  fullHeight?: boolean;
  loading: boolean;
  totalCount: number;
}

export const Table = ({ deviceCommands, fullHeight = false, loading, totalCount }: TableProps) => {
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<DeviceCommand | null>(null);

  const handleOpenDialog = useCallback((row: DeviceCommand) => {
    setSelectedRow(row);
    setDetailsDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDetailsDialogOpen(false);
    setSelectedRow(null);
  }, []);

  const columns = useColumns(handleOpenDialog);
  const [showColumns, setShowColumns] = useState<GridColumnVisibilityModel>({});

  const theme = useTheme();
  const isSmallAndDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumAndDown = useMediaQuery(theme.breakpoints.down('md'));

  const { paginationModel, setPageParams } = usePaginationParams();

  useEffect(() => {
    setShowColumns((x) => ({
      ...x,
      actions: !isSmallAndDown,
      createdAt: !isSmallAndDown,
      expiresAt: !isMediumAndDown,
    }));
  }, [isMediumAndDown, isSmallAndDown]);

  const onPaginationModelChange = useCallback(
    ({ page, pageSize }: GridPaginationModel) => {
      setPageParams({ page: page + 1, perPage: pageSize });
    },
    [setPageParams],
  );

  return (
    <>
      <SeparatedDataGrid
        autoHeight={!fullHeight}
        checkboxSelection={false}
        columns={columns}
        columnVisibilityModel={showColumns}
        disableRowSelectionOnClick
        loading={loading}
        onColumnVisibilityModelChange={setShowColumns}
        onPaginationModelChange={onPaginationModelChange}
        onRowClick={(params: GridRowParams<DeviceCommand>) => handleOpenDialog(params.row)}
        paginationMode="server"
        paginationModel={paginationModel}
        rowCount={totalCount}
        rows={deviceCommands}
        slots={{
          columnResizeIcon: () => null,
          pagination: DataGridPagination,
          noRowsOverlay: NoRowsOverlay,
        }}
        sx={
          fullHeight
            ? {
                height: '100%',
                '& .MuiDataGrid-footerContainer': {
                  minHeight: 52,
                  maxHeight: 52,
                },
                '& .MuiDataGrid-scrollbar--vertical': {
                  display: 'block',
                  top: 40,
                  height: 'calc(100% - 40px)',
                },
              }
            : undefined
        }
      />
      <DetailsDialog open={detailsDialogOpen} onClose={handleCloseDialog} row={selectedRow} />
    </>
  );
};
